import React, { useEffect, useRef, useState } from 'react';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Schema, DOMParser, DOMSerializer } from 'prosemirror-model';
import { schema } from 'prosemirror-schema-basic';
import { addListNodes } from 'prosemirror-schema-list';
import { exampleSetup } from 'prosemirror-example-setup';
import 'prosemirror-menu/style/menu.css';
import 'prosemirror-example-setup/style/style.css';
import './ProseMirrorEditor.scss';

// Create custom schema with mention & emoji support
const mentionNodeSpec = {
    inline: true,
    group: 'inline',
    selectable: false,
    atom: true,
    attrs: {
        id: {},
        name: {}
    },
    parseDOM: [{
        tag: 'span[data-mention-id]',
        getAttrs: dom => ({
            id: dom.getAttribute('data-mention-id'),
            name: dom.getAttribute('data-mention-name')
        })
    }],
    toDOM(node) {
        const { id, name } = node.attrs;
        return [
            'span',
            {
                class: 'pm-mention',
                'data-mention-id': id,
                'data-mention-name': name
            },
            `@${name}`
        ];
    }
};

const emojiNodeSpec = {
    inline: true,
    group: 'inline',
    selectable: false,
    atom: true,
    attrs: {
        char: {}
    },
    parseDOM: [{
        tag: 'span.pm-emoji',
        getAttrs: dom => ({
            char: dom.getAttribute('data-emoji') || dom.textContent || ''
        })
    }],
    toDOM(node) {
        const { char } = node.attrs;
        return [
            'span',
            {
                class: 'pm-emoji',
                'data-emoji': char
            },
            char
        ];
    }
};

let baseNodes = addListNodes(schema.spec.nodes, 'paragraph block*', 'block');
baseNodes = baseNodes.addBefore('text', 'mention', mentionNodeSpec);
baseNodes = baseNodes.addBefore('text', 'emoji', emojiNodeSpec);

const mySchema = new Schema({
    nodes: baseNodes,
    marks: schema.spec.marks
});

const ProseMirrorEditor = ({
    currentUser,
    projectUsers = [],
    onSubmit,
    placeholder = "Add a comment",
    onCancel,
    initialValue = ''
}) => {
    const editorRef = useRef(null);
    const viewRef = useRef(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionQuery, setMentionQuery] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [mentionedUsers, setMentionedUsers] = useState([]);
    
    const emojiPickerRef = useRef(null);
    const mentionDropdownRef = useRef(null);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);
    // Track where the current @mention started (absolute doc position)
    const mentionStartRef = useRef(null);

    // Common emojis
    const emojis = [
        '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
        '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙',
        '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔',
        '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥',
        '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮',
        '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '👏', '🙌', '👐',
        '❤️', '💛', '💚', '💙', '💜', '🖤', '💔', '❣️', '💕', '💞',
        '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉',
        '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈', '🥉', '⚽', '🏀',
        '✅', '❌', '⚠️', '🔔', '📢', '💬', '📝', '🔍', '⭐', '🔥'
    ];

    // Helper: extract unique mentions from a ProseMirror document
    const extractMentionsFromDoc = (doc) => {
        const mentionsMap = {};
        doc.descendants((node) => {
            if (node.type === mySchema.nodes.mention) {
                const id = node.attrs.id;
                const name = node.attrs.name;
                if (id && !mentionsMap[id]) {
                    mentionsMap[id] = { id, name };
                }
            }
        });
        return Object.values(mentionsMap);
    };

    // Initialize ProseMirror editor
    useEffect(() => {
        if (!editorRef.current) return;

        // Create initial state
        const state = EditorState.create({
            schema: mySchema,
            plugins: exampleSetup({ schema: mySchema, menuBar: false })
        });

        // Create view
        const view = new EditorView(editorRef.current, {
            state,
            dispatchTransaction(transaction) {
                const newState = view.state.apply(transaction);
                view.updateState(newState);
                
                // Check for @ mentions
                checkForMentions(newState);

                // Keep mentionedUsers in sync with actual mention nodes
                const mentionsInDoc = extractMentionsFromDoc(newState.doc);
                setMentionedUsers(mentionsInDoc);
            },
            handleDOMEvents: {
                focus: () => {
                    setIsFocused(true);
                    return false;
                },
                blur: () => {
                    setIsFocused(false);
                    return false;
                }
            }
        });

        viewRef.current = view;

        // Set initial content if provided
        if (initialValue) {
            const dom = document.createElement('div');
            dom.innerHTML = initialValue;
            const doc = DOMParser.fromSchema(mySchema).parse(dom);
            const newState = EditorState.create({
                doc,
                schema: mySchema,
                plugins: exampleSetup({ schema: mySchema, menuBar: false })
            });
            view.updateState(newState);
        }

        return () => {
            if (viewRef.current) {
                viewRef.current.destroy();
            }
        };
    }, []);

    // Check for @ mentions in editor
    const checkForMentions = (state) => {
        const { selection } = state;
        const { $from } = selection;
        const parent = $from.parent;

        // Text from start of this block node up to cursor
        const textBefore = parent.textBetween(0, $from.parentOffset, '\n', '\n');
        const lastAtIndex = textBefore.lastIndexOf('@');

        if (lastAtIndex === -1) {
            setShowMentionDropdown(false);
            setMentionQuery('');
            mentionStartRef.current = null;
            return;
        }

        const rawQuery = textBefore.slice(lastAtIndex + 1);

        if (/^[\w\s]*$/.test(rawQuery) && rawQuery.length < 50) {
            const trimmed = rawQuery.trimStart();
            setMentionQuery(trimmed);
            setShowMentionDropdown(true);
            // Absolute doc position where @ starts
            mentionStartRef.current = $from.start() + lastAtIndex;
        } else {
            setShowMentionDropdown(false);
            mentionStartRef.current = null;
        }
    };

    // Insert emoji
    const insertEmoji = (emoji) => {
        if (!viewRef.current) return;
        
        const { state, dispatch } = viewRef.current;
        const { selection } = state;
        const { $from } = selection;

        const emojiType = state.schema.nodes.emoji;
        if (!emojiType) return;

        const emojiNode = emojiType.create({ char: emoji });
        const transaction = state.tr.insert($from.pos, emojiNode);
        dispatch(transaction);
        
        setShowEmojiPicker(false);
        viewRef.current.focus();
    };

    // Insert mention as a span-like inline node with attrs & style
    const insertMention = (user) => {
        if (!viewRef.current) return;
        
        const { state, dispatch } = viewRef.current;
        const { selection } = state;
        const { $from } = selection;

        if (mentionStartRef.current == null) return;

        const startPos = mentionStartRef.current;
        const endPos = $from.pos;

        if (startPos >= endPos) return;

        // Build mention node
        const userId = user.ID || user.id;
        const userName = user.display_name || user.name;

        const mentionNodeType = state.schema.nodes.mention;
        if (!mentionNodeType) return;

        const mentionNode = mentionNodeType.create({ id: userId, name: userName });
        const spaceNode = state.schema.text(' ');

        const transaction = state.tr
            .delete(startPos, endPos)
            .insert(startPos, mentionNode)
            .insert(startPos + mentionNode.nodeSize, spaceNode);

        dispatch(transaction);

        // Reset mention tracking
        mentionStartRef.current = null;
        setMentionQuery('');
        
        setShowMentionDropdown(false);
        viewRef.current.focus();
    };

    // Handle file upload
    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        files.forEach(file => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const newAttachment = {
                    id: Date.now() + Math.random(),
                    file: file,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    preview: file.type.startsWith('image/') ? event.target.result : null
                };
                setAttachments([...attachments, newAttachment]);
            };
            reader.readAsDataURL(file);
        });
        e.target.value = '';
    };

    // Remove attachment
    const removeAttachment = (id) => {
        setAttachments(attachments.filter(att => att.id !== id));
    };

    // Get filtered users for mention
    const getFilteredUsers = () => {
        if (!mentionQuery) return projectUsers.slice(0, 50);
        const query = mentionQuery.toLowerCase();
        return projectUsers.filter(user => {
            const name = (user.display_name || user.name || '').toLowerCase();
            const email = (user.user_email || '').toLowerCase();
            return name.includes(query) || email.includes(query);
        }).slice(0, 50);
    };

    // Handle submit
    const handleSubmit = () => {
        if (!viewRef.current) return;
        
        const { state } = viewRef.current;
        const textContent = state.doc.textContent.trim();
        const htmlContent = getHTMLContent();
        
        if (textContent || attachments.length > 0) {
            const mentionsFromDoc = extractMentionsFromDoc(state.doc);
            onSubmit({
                content: textContent,
                htmlContent: htmlContent,
                mentions: mentionsFromDoc,
                attachments: attachments
            });
            
            // Reset
            setAttachments([]);
            setMentionedUsers([]);
            setShowEmojiPicker(false);
            setShowMentionDropdown(false);
            
            // Clear editor
            const emptyState = EditorState.create({
                schema: mySchema,
                plugins: exampleSetup({ schema: mySchema, menuBar: false })
            });
            viewRef.current.updateState(emptyState);
        }
    };

    // Get HTML content from ProseMirror state
    const getHTMLContent = () => {
        if (!viewRef.current) return '';
        const { state } = viewRef.current;
        const div = document.createElement('div');
        const serializer = DOMSerializer.fromSchema(mySchema);
        const fragment = serializer.serializeFragment(state.doc.content);
        div.appendChild(fragment);
        return div.innerHTML;
    };

    // Handle key events
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSubmit();
        } else if (e.key === 'Escape') {
            if (showEmojiPicker) {
                setShowEmojiPicker(false);
            } else if (showMentionDropdown) {
                setShowMentionDropdown(false);
            } else if (onCancel) {
                onCancel();
            }
        }
    };

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target) &&
                !event.target.closest('.prosemirror-emoji-btn')) {
                setShowEmojiPicker(false);
            }
            if (mentionDropdownRef.current && !mentionDropdownRef.current.contains(event.target)) {
                setShowMentionDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredUsers = getFilteredUsers();
    const hasContent = (viewRef.current?.state?.doc?.textContent || '').trim() || attachments.length > 0;

    return (
        <div className="prosemirror-comment-editor" onKeyDown={handleKeyDown}>
            <div className="prosemirror-editor-wrapper">
                {/* User Avatar */}
                {currentUser && (
                    <div className="prosemirror-comment-avatar">
                        {currentUser.image || currentUser.avatar_url ? (
                            <img 
                                src={currentUser.image || currentUser.avatar_url} 
                                alt={currentUser.display_name || currentUser.name || 'You'}
                            />
                        ) : (
                            <div className="prosemirror-comment-avatar-placeholder">
                                {(currentUser.display_name || currentUser.name || 'U').charAt(0).toUpperCase()}
                            </div>
                        )}
                    </div>
                )}

                {/* Editor Container */}
                <div className="prosemirror-editor-container">
                    {/* ProseMirror Editor */}
                    <div
                        ref={editorRef}
                        className={`prosemirror-editor-content ${isFocused ? 'focused' : ''}`}
                    />

                    {/* Attachments Preview */}
                    {attachments.length > 0 && (
                        <div className="prosemirror-attachments">
                            {attachments.map(att => (
                                <div key={att.id} className="prosemirror-attachment-item">
                                    {att.preview ? (
                                        <div className="prosemirror-attachment-image">
                                            <img src={att.preview} alt={att.name} />
                                            <button
                                                type="button"
                                                className="prosemirror-attachment-remove"
                                                onClick={() => removeAttachment(att.id)}
                                            >
                                                <i className="fa-solid fa-times"></i>
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="prosemirror-attachment-file">
                                            <i className="fa-solid fa-file"></i>
                                            <span className="prosemirror-attachment-name">{att.name}</span>
                                            <button
                                                type="button"
                                                className="prosemirror-attachment-remove"
                                                onClick={() => removeAttachment(att.id)}
                                            >
                                                <i className="fa-solid fa-times"></i>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Toolbar */}
                    <div className="prosemirror-toolbar">
                        <div className="prosemirror-toolbar-left">
                            <button
                                type="button"
                                className="prosemirror-toolbar-btn"
                                onClick={() => fileInputRef.current?.click()}
                                title="Add file"
                            >
                                <i className="fa-solid fa-plus"></i>
                            </button>
                            <button
                                type="button"
                                className="prosemirror-toolbar-btn"
                                onClick={() => imageInputRef.current?.click()}
                                title="Add image"
                            >
                                <i className="fa-solid fa-image"></i>
                            </button>
                            <button
                                type="button"
                                className="prosemirror-toolbar-btn prosemirror-emoji-btn"
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                title="Add emoji"
                            >
                                <i className="fa-regular fa-face-smile"></i>
                            </button>
                            <button
                                type="button"
                                className="prosemirror-toolbar-btn"
                                onClick={() => {
                                    if (viewRef.current) {
                                        const { state, dispatch } = viewRef.current;
                                        const { tr } = state;
                                        const mentionText = state.schema.text('@');
                                        dispatch(tr.insert(state.selection.$from.pos, mentionText));
                                        viewRef.current.focus();
                                    }
                                }}
                                title="Mention someone"
                            >
                                <i className="fa-solid fa-at"></i>
                            </button>
                        </div>
                        <div className="prosemirror-toolbar-right">
                            <span className="prosemirror-notification-text">
                                {mentionedUsers.length} {mentionedUsers.length === 1 ? 'person' : 'people'} will be notified
                            </span>
                            <button
                                type="button"
                                className={`prosemirror-submit-btn ${hasContent ? 'active' : ''}`}
                                onClick={handleSubmit}
                                disabled={!hasContent}
                            >
                                Comment
                            </button>
                        </div>
                    </div>

                    {/* Emoji Picker */}
                    {showEmojiPicker && (
                        <div ref={emojiPickerRef} className="prosemirror-emoji-picker">
                            <div className="prosemirror-emoji-grid">
                                {emojis.map((emoji, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="prosemirror-emoji-item"
                                        onClick={() => insertEmoji(emoji)}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Mention Dropdown */}
                    {showMentionDropdown && filteredUsers.length > 0 && (
                        <div ref={mentionDropdownRef} className="prosemirror-mention-dropdown">
                            {filteredUsers.map(user => (
                                <button
                                    key={user.ID || user.id}
                                    type="button"
                                    className="prosemirror-mention-item"
                                    onClick={() => insertMention(user)}
                                >
                                    <div className="prosemirror-mention-avatar">
                                        {user.image || user.avatar_url ? (
                                            <img src={user.image || user.avatar_url} alt={user.display_name || user.name} />
                                        ) : (
                                            <div className="prosemirror-mention-avatar-placeholder">
                                                {(user.display_name || user.name || 'U').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="prosemirror-mention-info">
                                        <div className="prosemirror-mention-name">
                                            {user.display_name || user.name}
                                        </div>
                                        <div className="prosemirror-mention-id">
                                            ID: {user.ID || user.id}
                                        </div>
                                        {user.user_email && (
                                            <div className="prosemirror-mention-email">
                                                {user.user_email}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Hidden File Inputs */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileUpload}
            />
            <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileUpload}
            />
        </div>
    );
};

export default ProseMirrorEditor;

